/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — export-html.ts
   generateAll(), buildCoverPage(), buildTOC(), HTML statement rendering,
   clearAll()
   ═══════════════════════════════════════════════════════════════════════ */

import { state, SECTIONS, type FirmProfile } from './state';
import { meta, esc, hasPriorData, extractYear, el } from './utils';
import { trackEvent, blockIfSafeMode } from './config';
import { saveProject } from './data';
import { applyTemplateToStatements } from './templates';
import {
  buildBalanceSheet,
  buildIncomeStatement,
  buildEquityStatement,
  buildCashFlow,
} from './statements';

import {
  switchTab,
  buildTrialBalanceEditor,
  loadPriorYearFields,
  renderAJEEntries,
  updateEmptyStates,
  updateTabBadges,
} from './ui';

// ─── Print Cover Page ──────────────────────────────────────────────────────

/** Loads the firm profile from localStorage (matches auth.ts pattern). */
function loadFirmProfile(): FirmProfile {
  try {
    return JSON.parse(localStorage.getItem('noteflow-firmprofile-' + state.currentUserEmail) || '{}') || {};
  } catch { return {}; }
}

/**
 * Builds a professional cover page for print/PDF output.
 * Populates #print-cover-page with company name, title, period, dates, and firm name.
 */
export function buildCoverPage(): void {
  const container = el('print-cover-page');
  if (!container) return;

  const m = meta();
  const multi = hasPriorData();
  const priorYearOnly = m.priorPeriod ? (extractYear(m.priorPeriod) || m.priorPeriod) : '';
  const periodDisplay = multi && m.priorPeriod ? m.period + ' and ' + priorYearOnly : m.period;
  const firm = loadFirmProfile();

  let html = '';
  html += '<div class="cover-company">' + esc(m.company) + '</div>';
  html += '<div class="cover-title">Financial Statements</div>';
  html += '<div class="cover-divider"></div>';
  html += '<div class="cover-period">' + esc(periodDisplay) + '</div>';

  if (m.engagementDate) {
    html += '<div class="cover-date">Engagement Date: ' + esc(m.engagementDate) + '</div>';
  }

  const reportDate = m.reportDate || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  html += '<div class="cover-date">Report Date: ' + esc(reportDate) + '</div>';

  if (firm.firmName) {
    html += '<div class="cover-firm">' + esc(firm.firmName) + '</div>';
  }

  container.innerHTML = html;
}

// ─── Print Table of Contents ───────────────────────────────────────────────

/**
 * Builds a table of contents listing all generated statements for print output.
 * Populates #print-toc with a styled list of statements.
 */
export function buildTableOfContents(): void {
  const container = el('print-toc');
  if (!container) return;

  const entries: { label: string; page: number }[] = [
    { label: 'Balance Sheet', page: 3 },
    { label: 'Income Statement', page: 4 },
    { label: 'Statement of Changes in Equity', page: 5 },
    { label: 'Statement of Cash Flows', page: 6 },
    { label: 'Notes to Financial Statements', page: 7 },
  ];

  let html = '<div class="toc-title">Table of Contents</div>';
  html += '<ul class="toc-list">';
  for (const entry of entries) {
    html += '<li><span class="toc-label">' + esc(entry.label) + '</span><span class="toc-page">' + entry.page + '</span></li>';
  }
  html += '</ul>';

  container.innerHTML = html;
}

// ─── Generate All ──────────────────────────────────────────────────────────

export function generateAll(): void {
  trackEvent('statements_generated');
  try { buildBalanceSheet(); } catch (e) { console.error('Balance Sheet build failed:', e); }
  try { buildIncomeStatement(); } catch (e) { console.error('Income Statement build failed:', e); }
  try { buildEquityStatement(); } catch (e) { console.error('Equity Statement build failed:', e); }
  try { buildCashFlow(); } catch (e) { console.error('Cash Flow build failed:', e); }
  applyTemplateToStatements();
  try { buildCoverPage(); } catch (e) { console.error('Cover page build failed:', e); }
  try { buildTableOfContents(); } catch (e) { console.error('TOC build failed:', e); }
  updateEmptyStates();
  updateTabBadges();
  buildTrialBalanceEditor();
  switchTab('balance');
}

// ─── Clear ──────────────────────────────────────────────────────────────────

export function clearAll(): void {
  blockIfSafeMode('Clearing all data', function () { _clearAllInner(); });
}

function _clearAllInner(): void {
  SECTIONS.forEach(function (s: string) { state.currentData[s] = []; state.priorData[s] = []; });
  const statusEl = document.getElementById('import-status');
  if (statusEl) statusEl.innerHTML = '';
  // Reset file zone
  state.importFiles.combined = null;
  const z = document.getElementById('zone-combined');
  if (z) z.classList.remove('has-file');
  const hint = document.getElementById('zone-combined-hint');
  const name = document.getElementById('zone-combined-name');
  if (hint) hint.textContent = 'Drop CSV here or click to browse';
  if (name) name.textContent = '';
  // Reset new features
  state.ajeEntries = []; state.ajePosted = false; state.ajePrePostData = null;
  renderAJEEntries();
  loadPriorYearFields();
  buildTrialBalanceEditor();
  saveProject();
}
